<template>
    <div class="tasks-page-wrapper">
        <HLCardLeftLayout
            v-model="showTask"
            :autoCheckin="autoCheckin"
            :key="invalidateCounter"
            @input="onCardLayoutChanged"
            :loadingMenu="list_loading"
            :loadingMain="spot_solve_loading"
            :insetMain="inset"
            :singleView="singleView"
            :hideMenu="hideTaskList"
            :drawer="taskListDrawer"
            :transitionKey="selectedSpot ? selectedSpot.id : 0"
            :backgroundMenu="theme.backgroundColor"
        >
            <template #menu-handle>
                <EspTaskListHandle
                    :open="taskListDrawerOpen"
                    :openLabel="labelTasks"
                    :closeLabel="$t('taskListHide')"
                    @toggle="taskListDrawerOpen = !taskListDrawerOpen"
                />
            </template>
            <template #menu-title>
                <div class="menu-title-row">
                    <span class="menu-title-text grey--text text--darken-3" :style="{'color':theme.footerIconColor + '!important'}">{{ labelTasks }}</span>
                    <div class="menu-title-actions">
                        <HLSpotListActions
                            compactLayout
                            :mode="mode"
                            @refresh="onRefreshSpotList"
                            @switchListMode="onSwitchListMode"
                            @changeSortOrder="onChangeSortOrder"
                        />
                    </div>
                </div>
            </template>
            <template #menu>
                <HLSpotList
                    ref="spotList"
                    :spot="selectedSpot"
                    :mode="mode"
                    :sortOrder="sortOrder"
                    :eventId="selectedEventId"
                    @select="onSelectTaskFromList"
                />
            </template>
            <template #main-title>
            <span :class="inset ? 'white--text' : 'grey--text text--darken-3'" :style="{'color':theme.footerIconColor + '!important'}">
                {{ spotTitle }}
            </span>
            </template>
            <template #main>
                <EspAnswerAnimation
                    v-model="showAnswerAnimation"
                    :answerResultType="answerResultType"
                    :isMobil="isXsDevice"
                    class=""
                />
                <!-- Destroyed on close so embedded video/audio stops. singleView (QR, password and
                     auto-checkin spots via the taskpage store) shows the main pane without setting showTask. -->
                <HLSpotDetails
                    v-if="showTask || singleView"
                    v-show="!showAnswerAnimation"
                    id="hlSpotDetails"
                    :spot="selectedSpot"
                    :inset="inset"
                    :active="taskPanelActive"
                    :wizardStep="wizardStep"
                    :error="error"
                    class="px-0 px-sm-0"
                    @select="onOpenTask"
                    @input="onTaskInput"
                    @reloadList="reloadSpotListOnCantPlayAgain"
                />
            </template>
            <template #background>
                <EspMap
                    v-show="showmap"
                    ref="map"
                    name="map"
                    :loading="list_loading"
                    :event="selectedEvent"
                    :eventWaypoints="allActiveVisibleSpots"
                    :indoorMaps="selectedEvent.indoormaps"
                    :eventMapstyle="selectedEvent.mapstyle"
                    :hideTiles="!settingsShowMapTiles"
                    @select="onSelectTaskFromMap"
                />
            </template>
            <template #footer>
                <HLSpotDetailsFooter
                    ref="spotDetailsFooter"
                    class="pt-1 pa-0 px-1 px-sm-5 px-lg-10"
                    :spot="selectedSpot"
                    :taskInput="taskInput"
                    :error="error"
                    :active="!eventIsInactive"
                    :singleView="singleView"
                    :autoCheckin="autoCheckin"
                    :hideFooter="hideFooter"
                    :timezone="timezone"
                    @next="onGoNext"
                    @prev="onGoPrev"
                    @checkin="onCheckin"
                    @solve="onSolve"
                    @help="onHelp"
                    @requestHint="onRequestHint"
                    @close="onCloseTask"
                    @changedWizardStep="onChangedWizardStep"
                    :showNext="showNext"
                    :showPrev="showPrev"
                    :customNames="{
          labelTask: labelTask,
          labelTaskAac: labelTaskAac,
          labelTaskCheckin: labelTaskCheckin,
          labelTaskPass: labelTaskPass,
          labelTaskSolve: labelTaskSolve,
          labelStepperTaskAac:
            selectedEvent && selectedEvent.lbl_app_task_aac
              ? selectedEvent.lbl_app_task_aac
              : null,
          labelStepperTaskCheckin:
            selectedEvent && selectedEvent.lbl_app_task_checkin
              ? selectedEvent.lbl_app_task_checkin
              : null,
          labelStepperTaskSolve:
            selectedEvent && selectedEvent.lbl_app_task_solve
              ? selectedEvent.lbl_app_task_solve
              : null,
        }"
                />
            </template>

        </HLCardLeftLayout>

        <!-- GPS problem dialog -->
        <HLConfirm v-model="gpsDialog" v-bind="gpsDialogOptions"/>

        <!-- time limit confirmation before the checkin -->
        <HLConfirm
            v-model="timeLimitDialog"
            :title="labelTask"
            :text="timeLimitText"
            icon="mdi-timer-outline"
            :txtAgree="$t('ok')"
            :txtDismiss="$t('cancel')"
            @success="onTimeLimitConfirmed"
            @close="onTimeLimitClosed"
        />
    </div>
</template>

<script>

import CONFIG from "../../constants/config";
import {layoutMixin} from "../../utils/layout-mixin";
import {eventMixin} from "../../utils/event-mixin";
import {spotMixin} from "../../utils/spot-mixin";
import {geolocationMixin} from "../../utils/geolocation-mixin";
import {teamMixin} from "../../utils/team-mixin";
import {solutionMixin} from "../../utils/solution-mixin";
import {dateMixin} from "../../utils/date-mixin";
import {EventBus} from "../../components/spots/EventBus";
import {EventBusNotification} from "../../components/EventBusNotification";
import {isCantPlayAgainError} from "../../utils/request-error.mjs";
import {formatClock} from "../../utils/event-time.mjs";
import {mapGetters} from 'vuex';
import HLCardLeftLayout from "../../components/dashboard/HLCardLeftLayout";
import HLSpotList from "../../components/dashboard/HLSpotList";
import HLSpotListActions from "../../components/dashboard/HLSpotListActions";
import EspTaskListHandle from "../../components/dashboard/EspTaskListHandle";
import HLSpotDetails from "../../components/dashboard/HLSpotDetails";
import HLSpotDetailsFooter from "../../components/dashboard/HLSpotDetailsFooter";
import EspAnswerAnimation from "../../components/animation/EspAnswerAnimation";
import EspMap from "../../components/map/EspMap";
import HLConfirm from "../../components/ui/HLConfirm";

export default {
    mixins: [layoutMixin, eventMixin, spotMixin, geolocationMixin, dateMixin, teamMixin, solutionMixin],
    components: {
        HLCardLeftLayout,
        HLSpotList,
        HLSpotListActions,
        EspTaskListHandle,
        HLSpotDetails,
        HLSpotDetailsFooter,
        EspAnswerAnimation,
        EspMap,
        HLConfirm,
    },
    props: {
    },
    data() {
        return {
            taskListDrawerOpen: false,
            showTask: false,
            // keep-alive state; a singleView spot stays mounted while another page is shown
            pageActive: true,
            selectedSpot: null,
            sortOrder: { order: 'asc', option: 'byNumber' },
            mode: 0,
            wizardStep: 0,
            taskInput: null,
            taskInputServerData: null,
            inset: false,
            error: null,
            answerResultType: null,
            showAnswerAnimation: false,
            invalidateCounter: 0,
            centerSpotImmediatly: false,
            timeLimitDialog: false,
            timeLimitText: '',
            timeLimitResolve: null, // settles the promise of the open time limit dialog
        };
    },
    watch: {
        $route: {
            handler: function (route, oldRoute) {
                // entering the map page starts the drawer closed on phones and open on sm; the
                // handle changes it afterwards
                if (route && route.name == 'map' && (!oldRoute || oldRoute.name != 'map')) {
                    this.taskListDrawerOpen = this.isSmDevice;
                }
                if (this.selectedSpot) {
                    this.leaveSpot(this.selectedSpot.id);
                    // a real leave: the v-if destroy alone would only pause the step audio
                    this.$store.dispatch('taskaudio/stop');
                }
                this.showTask = false;
            },
            deep: true,
            immediate: true,
        },
        spot: {
            handler(val) {
                if (!val) {
                    return;
                }
                // a spot found by QR or password opens like a list tap, not as single view
                if (this.selectViaList) {
                    this.$store.commit('taskpage/resetState');
                    this.scrollToSpotInList(val.id);
                    this.onSelectTask(val.id);
                    return;
                }
                this.selectedSpot = val;
                // the single view shows the detail at once; a spot without an intro checks in first
                if (!this.autoCheckin && this.opensWithoutIntro(val) && !this.isCheckinTypeQr(val) && !this.isCheckinTypePasswort(val)) {
                    this.checkinSingleView();
                }
            },
            immediate: true,
            deep: true,
        },
        // the checkin actions count the spot shown right now as a match of its own code
        openSpotId: {
            handler(id) {
                this.$store.commit('taskpage/SetOpenSpotId', id);
            },
            immediate: true,
        },
        showTask(val) {
            if (!val) {
                //clear selection in list to prevent double click bugs
                this.$refs.spotList.clearSelect();
            }
        },
        selectedSpot(val, oldVal) {
            // switching to another spot (or to none) counts as leaving the old one
            if (oldVal && (!val || val.id !== oldVal.id)) {
                this.leaveSpot(oldVal.id);
            }
            // until the footer re-inits the wizard, a stale step would select the previous step's
            // audio of the new spot; not on a mere object swap of the same spot (refresh)
            if (!val || !oldVal || val.id !== oldVal.id) {
                this.wizardStep = 0;
            }
        },
        showAnswerAnimation(val, oldVal) {
            // The details are hidden while the animation runs, so the scroll from the text watcher in
            // HLSpotDetails is a no-op; position the result once the details are visible again.
            // Checked by animation type: spot.trials also stays > 0 after a correct solution.
            if (oldVal && !val) {
                this.$nextTick(() => {
                    if (this.answerResultType === CONFIG.USER_ANSWER_RESULT_TRY_AGAIN) {
                        this.scrollToBottom('hlSpotDetails'); // keep the input visible for the next try
                    } else {
                        this.scrollToTopOfView('hlSpotDetails');
                    }
                });
            }
        },
        coarseDialogPending() {
            this.maybeShowCoarseDialog();
        },
        // a pending coarse dialog waits until the shared GPS dialog and the checkin actions are closed
        gpsDialog(val) {
            if (!val) {
                this.maybeShowCoarseDialog();
            }
        },
        checkinActionOpen(val) {
            if (!val) {
                this.maybeShowCoarseDialog();
            }
        },
    },
    computed: {
        ...mapGetters('taskpage', ['singleView', 'hideFooter', 'autoCheckin', 'spot', 'selectViaList', 'checkinActionOpen']),

        // the details are only v-show-hidden during the answer animation, but the footer already
        // switched the step; without this the feedback audio would start under the animation
        taskPanelActive() {
            return this.pageActive && !this.showAnswerAnimation;
        },
        openSpotId() {
            return this.selectedSpot && (this.showTask || this.singleView) ? this.selectedSpot.id : null;
        },

        isMapRoute() {
            return !!(this.$route && this.$route.name == 'map');
        },
        // the mobile map page keeps the list as a drawer over the map, unless the event disables it
        taskListDrawer() {
            return this.isMapRoute && this.isSmAndDownDevice && !this.mapTaskListDisabled;
        },
        // the map page shows the list only as an open drawer, the task page always
        hideTaskList() {
            return this.isMapRoute && !(this.taskListDrawer && this.taskListDrawerOpen);
        },

        showmap() {
            let show = true;
            // Checking if showmap is present in the route params and setting this.showmap accordingly
            if (Object.prototype.hasOwnProperty.call(this.$route.params,'showmap')) {
                if (this.$route.params.showmap === 'map') {
                    show = true;
                } else if (this.$route.params.showmap === 'nomap') {
                    show = false;
                } else {
                    // If showmap has any other value, set it based on the computed property shouldShowMap
                    show = this.shouldShowMap;
                }
            } else {
                // If showmap is not present in the route params, set it based on the computed property shouldShowMap
                show = this.shouldShowMap;
            }
            return show;
        },
        spotTitle() {
            return this.getSpotTitle(this.selectedSpot);
        },
        showNext() {
            if (
                !this._.isEmpty(this.selectedSpot) &&
                this._.isArray(this.allActiveVisibleSpots)
            ) {
                return (
                    this._.findIndex(this.allActiveVisibleSpots, [
                        'id',
                        this.selectedSpot.id,
                    ]) <
                    this.allActiveVisibleSpots.length - 1
                );
            }
            return false;
        },
        showPrev() {
            if (
                !this._.isEmpty(this.selectedSpot) &&
                this._.isArray(this.allActiveVisibleSpots)
            ) {
                return (
                    this._.findIndex(this.allActiveVisibleSpots, [
                        'id',
                        this.selectedSpot.id,
                    ]) > 0
                );
            }
            return false;
        },
        hasTaskInput() {
            return !this._.isEmpty(this.taskInput);
        },
        hasCantPlayAgainError() {
            return (
                !this._.isEmpty(this.error) && this.error.error == 'cant_play_again'
            );
        },
    },
    methods: {

        onCardLayoutChanged(val) {
            if (!val) {
                this.centerSpotInMap(this.selectedSpot); // Center last selected Spot
                this.onCloseTask();
            }
        },
        onCloseTask() {
            if (this.selectedSpot) {
                this.leaveSpot(this.selectedSpot.id);
            }
            EventBus.$emit(CONFIG.EVENT_BUS_CLOSE_SPOT);
        },
        onSelectTaskFromMap(spotId) {
            this.scrollToSpotInList(spotId);
            this.onSelectTask(spotId);
        },
        onSelectTaskFromList(spotId) {
            this.getSpotById(spotId).then((spot) => {
                if (this.centerSpotImmediatly || !this.showTask) {
                    this.centerSpotInMap(spot);
                }
                this.onSelectTask(spotId);
            });
        },
        // a QR or password spot without a description asks for its code first (bound to the
        // spot), unless the code was already given through the checkin actions
        onSelectTask(spotId) {
            if (spotId) {
                this.getSpotById(spotId).then((spot) => {
                    if (!this.canOpenTaskView(spot)) {
                        this.$refs.spotList.clearSelect();
                    } else if (!this.opensWithoutIntro(spot)) {
                        this.openTask(spot);
                    } else if (this.isCheckinTypeQr(spot) && !this.isCheckinVerified(spot)) {
                        this.$refs.spotList.clearSelect();
                        EventBus.$emit(CONFIG.EVENT_BUS_OPEN_CHECKIN_ACTION, { type: 'qr', spot });
                    } else if (this.isCheckinTypePasswort(spot) && !this.isCheckinVerified(spot)) {
                        this.$refs.spotList.clearSelect();
                        EventBus.$emit(CONFIG.EVENT_BUS_OPEN_CHECKIN_ACTION, { type: 'password', spot });
                    } else {
                        this.checkinAndOpen(spot);
                    }
                });
            }
        },
        // the code of the spot shown right now came in through the checkin actions
        onCheckinOpenSpot(spot) {
            if (this.selectedSpot && spot && this.selectedSpot.id === spot.id && !this.spotIsCheckedIn(this.selectedSpot)) {
                this.performCheckinAndGoToWizardStep();
            }
        },
        openTask(spot) {
            this.selectedSpot = spot;
            this.showTask = true;
            this.taskInput = null;
            this.taskInputServerData = null;
            this.error = null;
        },
        /**
         * A spot without a description has no info step to read, so it checks in on opening like
         * in the native app (QR and password spots open their scanner or input instead). Played and
         * checked-in spots and spots a slave may not check in open as they are.
         */
        opensWithoutIntro(spot) {
            return !this.spotHasDescription(spot)
                && !this.spotAlreadyTouched(spot)
                && !this.spotIsCheckedIn(spot)
                && !this.checkinBlockedForSlave(spot);
        },
        // the detail opens only once the local checkin stands; a refused checkin or a dismissed
        // time limit dialog leaves the player in the list
        async checkinAndOpen(spot) {
            this.showTask = false; // a previously open detail must not show the new spot before its checkin
            this.selectedSpot = spot;
            try {
                await this.performCheckin();
            } catch (error) {
                this.selectedSpot = null;
                this.$refs.spotList.clearSelect();
                return;
            }
            this.openTask(spot);
            this.goToStepAfterCheckin();
        },
        // the single view already shows the detail, so a refused or dismissed checkin closes it
        async checkinSingleView() {
            try {
                await this.performCheckin();
                this.goToStepAfterCheckin();
            } catch (error) {
                this.onCloseTask();
            }
        },

        // Shows the coarse-location hint once per page load. It shares the HLConfirm with the other
        // GPS dialogs, so it never replaces an open one or sits on top of the QR/password overlays.
        maybeShowCoarseDialog() {
            if (!this.coarseDialogPending || this.gpsDialog || this.checkinActionOpen) {
                return;
            }
            this.$store.commit('geolocation/SET_COARSE_DIALOG_SHOWN');
            this.showGpsCoarseDialog();
        },

        // without a position the "too far away" message would mislead; explain the GPS state instead
        notifyNotInRange(spot){
            if (this.isGpsDenied || spot.distanceToCurrentPosition == null) {
                this.showGpsProblemDialog();
            } else {
                this.showNotInRangeDialog(spot);
            }
        },

        canOpenTaskView(spot){
            // console.log('spot: ', spot);
            if (this.spotAlreadyTouched(spot)) {
                // console.log('spotAlreadyTouched');
                return true;
            }
            else if (this.eventIsInactive) {
                // console.log('eventIsInactive');
                EventBusNotification.$emit(CONFIG.EVENT_BUS_NOTIFICATION_SHOW, this.$t('taskEventNotActive', { event: this.$t('event') }));
                return false;
            }
            else if (this.isAutoCheckinSpot(spot)) {
                // opens only through the auto-checkin, never by tap, even with a description
                if (this.spotIsInactive(spot)) {
                    EventBusNotification.$emit(CONFIG.EVENT_BUS_NOTIFICATION_SHOW, this.$t('taskInactive'));
                } else {
                    this.notifyNotInRange(spot);
                }
                return false;
            }
            else if (this.spotHasDescription(spot)) {
                // console.log('spotHasDescription');
                return true;
            }
            else if (this.spotIsInactive(spot)) {
                // console.log('spotIsInactive');
                EventBusNotification.$emit(CONFIG.EVENT_BUS_NOTIFICATION_SHOW, this.$t('taskInactive'));
                return false;
            }
            else if (this.spotIsCheckedIn(spot)) {
                // console.log('spotIsCheckedIn');
                return true;
            }
            else if (this.spotMustBeInRange(spot, this.settingsGpsDisabled) && !this.spotIsInRange(spot)) {
                // console.log('spotMustBeInRange');
                this.notifyNotInRange(spot);
                return false;
            }
            else {
                return true;
            }
        },

        onOpenTask(spot) {
            this.showAnswerAnimation = false;
            this.selectedSpot = spot;
        },
        onChangeSortOrder(order) {
            this.sortOrder = order;
        },
        onSwitchListMode(mode) {
            this.mode = mode;
        },
        onRefreshSpotList() {
            this.showTask = false;
            this.selectedSpot = null;
            this.onCloseTask()
            this.reloadSpotList();
        },
        onGoNext() {
            this.$refs.spotList.next();
        },
        onGoPrev() {
            this.$refs.spotList.prev();
        },
        onChangedWizardStep(step) {
            this.wizardStep = step;
        },
        onCheckin() {
            if (this.selectedSpot && this.selectedEvent) {
                this.performCheckinAndGoToWizardStep();
            }
        },

        // synchronous checks before a checkin; the time limit question follows in performCheckin()
        canCheckin(){
            // console.log('this.selectedSpot: ', this.selectedSpot);
            if (this.spotIsCheckedIn(this.selectedSpot)) {
                // console.log('spotIsCheckedIn');
                return true;
            }
            else if (this.eventIsInactive) {
                // console.log('eventIsInactive');
                EventBusNotification.$emit(CONFIG.EVENT_BUS_NOTIFICATION_SHOW, this.$t('taskEventNotActive', { event: this.$t('event') }));
                return false;
            }
            else if (this.spotIsInactive(this.selectedSpot)) {
                // console.log('spotIsInactive');
                EventBusNotification.$emit(CONFIG.EVENT_BUS_NOTIFICATION_SHOW, this.$t('taskInactive'));
                return false;
            }
            else if (this.spotMustBeInRange(this.selectedSpot, this.settingsGpsDisabled) && !this.spotIsInRange(this.selectedSpot)) {
                // console.log('spotMustBeInRange');
                this.notifyNotInRange(this.selectedSpot);
                return false;
            }
            else{
                // console.log('defaultCheckIn');
                return true;
            }
        },

        // resolves true when the player confirms the time limit, false when the dialog is dismissed
        confirmTimeLimit(spot) {
            return new Promise((resolve) => {
                this.timeLimitText = this.$t('taskTimeWarning', { time: formatClock(spot.answer_time_limit), task: this.labelTask });
                this.timeLimitResolve = resolve;
                this.timeLimitDialog = true;
            });
        },
        onTimeLimitConfirmed() {
            this.settleTimeLimit(true);
        },
        // HLConfirm emits close after success as well; the answer is settled by then
        onTimeLimitClosed() {
            this.settleTimeLimit(false);
        },
        settleTimeLimit(confirmed) {
            const resolve = this.timeLimitResolve;
            this.timeLimitResolve = null;
            if (resolve) {
                resolve(confirmed);
            }
        },

        centerSpotInMap(spotId) {
            if (this.showmap && this.$refs.map) {
                this.$refs.map.centerSpot(spotId);
            }
        },
        scrollToSpotInList(spotId) {
            if (this.$refs.spotList) {
                this.$refs.spotList.centerSpot(spotId);
            }
        },

        updateSpotOnMap(spot) {
            if (this.showmap && this.$refs.map) {
                this.$refs.map.updateSpot(spot);
            }
        },

        showAnswerResults(spot) {
            this.answerResultType = this.isInfoSpot(spot)
                ? CONFIG.USER_INFO_SPOT_PASSED
                : this.spotIsSolved(spot)
                    ? CONFIG.USER_ANSWER_RESULT_SOLVED
                    : this.hasTrialsLeft(spot)
                        ? CONFIG.USER_ANSWER_RESULT_TRY_AGAIN
                        : CONFIG.USER_ANSWER_RESULT_FAILED;
            this.showAnswerAnimation = true;
        },
        reloadSpotListOnCantPlayAgainAndEnabledServerSync(force = false) {
            this.$nextTick(() => {
                if (this.hasCantPlayAgainError && (force || this.serverSync)) {
                    this.reloadSpotList();
                }
            });
        },
        reloadSpotListOnCantPlayAgain() {
            this.reloadSpotListOnCantPlayAgainAndEnabledServerSync(true);
        },
        reloadSpotList() {
            this.getSpotsByEventId(this.selectedEventId).then(() => {
                this.getPlayerinfo();
                //FORCE to close active spot and open it again
                this.refreshView();
            });
        },
        refreshView() {
            if (!this._.isEmpty(this.selectedSpot) && this.showTask) {
                this.showTask = false;
                this.$nextTick(() => {
                    this.onSelectTask(this.selectedSpot.id);
                });
            }
        },
        invalidateView() {
            this.invalidateCounter++;
        },

        onSolve(sendEmptySolution, solution) {
            // console.log('onSolve solution: ', solution);
            if (
                this.selectedSpot &&
                this.selectedEvent &&
                this.isTeamLeaderInMasterSlaveModeOrIsSingleMode
            ) {

                if( !this._.isEmpty(solution) ){
                    this.onTaskInput(solution, this.getServerData(solution));
                }

                sendEmptySolution = this._.isBoolean(sendEmptySolution)
                    ? sendEmptySolution
                    : false;
                if (sendEmptySolution) {
                    if (!this.hasTaskInput) {
                        this.onTaskInput('', this.getServerData(''));
                        //Send offset of 1 sec if countdown ended to prevent try again animation instead of failed
                        this.taskInputServerData.date = this.getCurrentDateTimeWithOffset(
                            1,
                            this.selectedEvent.timezone,
                        );
                    }
                }

                const spot = this.selectedSpot;
                this.solveSpot(
                    spot.id,
                    this.selectedEventId,
                    this.selectedEvent.timezone,
                    this.taskInputServerData,
                    (ex) => {
                        this.onError(ex);
                        //this.reloadSpotListOnCantPlayAgainAndEnabledServerSync();
                    },
                    () => {
                        //Additional Success callback
                        this.notifyMasterSlaveTeamPlayersForSolve(spot); //send broadcast to inform other users of same team
                    },
                )
                    .then((solvedSpot) => {
                        EventBus.$emit(CONFIG.EVENT_BUS_UPDATE_SPOT_ON_MAP, spot);
                        this.getPlayerinfo(); // Refresh points //TODO replace by offline berechnung
                        this.showAnswerResults(solvedSpot);
                        if (this.serverSync) {
                            // Refresh spot list in sync mode
                            //this.getSpotsByEventId({eventId:this.selectedEventId,silent:true});
                        }
                    })
                    .catch((err) => {
                        // no result animation without a server answer; the player sees the error
                        // notification and keeps the input for another try
                        console.log(err);
                        this.onError(err);
                        if (this.isInfoSpot(spot) && !isCantPlayAgainError(err)) {
                            this.undoInfoSpotCheckin(spot);
                        }
                    });
            }
        },
        /**
         * Takes the local checkin of an info spot back after its solve got no server answer, so
         * the spot stays playable. In the auto-checkin overlay the spot is parked until the next
         * successful event refresh and the overlay closed (it would be re-checked-in at once
         * otherwise); elsewhere the wizard returns to the checkin step.
         */
        undoInfoSpotCheckin(spot) {
            const inOverlay = this.autoCheckin;
            if (inOverlay) {
                this.suppressAutoCheckin(spot.id);
                this.onCloseTask();
            }
            this.resetCheckinSpot(spot.id).then(() => {
                if (!inOverlay && this.$refs.spotDetailsFooter) {
                    this.$refs.spotDetailsFooter.goToWizardStep(CONFIG.TASK_STEPPER_CHECKIN);
                }
            });
        },
        onTaskInput(val, serverData) {
            this.taskInput = val;
            this.taskInputServerData = serverData;
        },

        onError(val) {
            if (val && val.error) {
                this.error = val;
            }
            if (
                val &&
                !this.hasCantPlayAgainError &&
                (val.isAxiosError ||
                    this._.isEmpty(val.response) ||
                    !val.response.status)
            ) {
                EventBusNotification.$emit(CONFIG.EVENT_BUS_NOTIFICATION_SHOW, this.$t('msgTechnicalError'));
            }
            console.warn(val);
        },
        onHelp(val) {
            let searchedSpot = this.getSpotByPassword(val);
            //let foundSpot=!this._.isEmpty(searchedSpot);

            if (this.$refs.spotDetailsFooter) {
                this.$refs.spotDetailsFooter.setHelpSystemState(searchedSpot);
            }
        },
        onRequestHint(hintId) {
            // console.log("hintId", hintId);
            if (
                this.selectedSpot &&
                this.selectedEvent &&
                this.isTeamLeaderInMasterSlaveModeOrIsSingleMode &&
                !this.isLoadingHintRequest
            ) {
                this.taskInputServerData = {};
                this.taskInputServerData.date = this.getCurrentDateTimeWithOffset(
                    1,
                    this.selectedEvent.timezone,
                );

                this.requestHint(
                    hintId,
                    this.selectedSpot.id,
                    this.selectedEventId,
                    this.selectedEvent.timezone,
                    this.taskInputServerData,
                    (ex) => {
                        this.onError(ex);
                        //this.reloadSpotListOnCantPlayAgainAndEnabledServerSync();
                    },
                    () => {
                        //Additional Success callback
                        this.notifyMasterSlaveTeamPlayersForSolve(this.selectedSpot); //send broadcast to inform other users of same team
                    },
                )
                    .then(() => {
                        EventBus.$emit(CONFIG.EVENT_BUS_UPDATE_SPOT_ON_MAP, this.selectedSpot);
                        this.getPlayerinfo(); // Refresh points //TODO replace by offline berechnung

                        // TODO notify spot update for hint view
                    })
                    .catch((err) => {
                        console.log(err);
                        this.onError(err);
                    });
            }
        },

        async performCheckinAndGoToWizardStep() {
            try {
                await this.performCheckin();
                this.goToStepAfterCheckin();
            } catch (error) {
                console.log('Checkin failed:', error);
            }
        },
        // checkin_time alone does not re-init the footer wizard, so the step after a checkin is set
        // here: the task of a quest, the feedback of an info spot (passed with its checkin)
        goToStepAfterCheckin() {
            this.$nextTick(() => {
                if (this.$refs.spotDetailsFooter) {
                    this.$refs.spotDetailsFooter.goToWizardStep(
                        this.isInfoSpot(this.selectedSpot) ? CONFIG.TASK_STEPPER_AAC : CONFIG.TASK_STEPPER_TASK,
                    );
                }
            });
        },
        /**
         * Local checkin of the selected spot after the checks of canCheckin(). A spot with a time
         * limit asks for confirmation first, except in the auto-checkin overlay. Rejects when a
         * check fails or the player dismisses the question. Info spots are solved right after the
         * checkin, slaves in teamleader mode check in locally without sending.
         */
        async performCheckin() {
            const spot = this.selectedSpot;
            if (!spot || !this.selectedEvent || !this.canCheckin()) {
                throw new Error('Cannot checkin');
            }
            if (!this.autoCheckin && !this.spotIsCheckedIn(spot) && this.spotHasAnswerTimeLimit(spot)) {
                const confirmed = await this.confirmTimeLimit(spot);
                if (!confirmed) {
                    throw new Error('Checkin cancelled');
                }
            }
            return new Promise((resolve, reject) => {
                //Allow checkin in master/slave mode. But do not send
                if (this.isSlaveInMasterSlaveMode) {
                    this.checkinSpot(
                        spot.id,
                        this.selectedEventId,
                        this.selectedEvent.timezone,
                        true,
                    );
                    resolve();
                    return;
                }

                // info spots do not need checkin, instead solve directly after 'checkin'
                let doNotSend = this.isInfoSpot(spot);
                this.checkinSpot(
                    spot.id,
                    this.selectedEventId,
                    this.selectedEvent.timezone,
                    doNotSend,
                    (ex) => {
                        this.onError(ex);
                        reject(ex);
                    },
                ).then((checkedInSpot) => {
                        if (this.isInfoSpot(checkedInSpot)) {
                            this.onSolve(true);
                        } else {
                            this.notifyMasterSlaveTeamPlayersForCheckin(checkedInSpot);
                        }
                        resolve(checkedInSpot);
                    })
                    .catch((ex) => {
                        console.log('check in error');
                        console.log(ex);
                        this.onError(ex);
                        reject(ex);
                    });
            });
        },
        registerEventListener() {
            EventBus.$on(CONFIG.EVENT_BUS_REFRESH_SPOT, () => {
                this.$nextTick(() => {
                    this.refreshView();
                });
            });
            EventBus.$on(CONFIG.EVENT_BUS_UPDATE_SPOT_ON_MAP, (spot) => {
                this.updateSpotOnMap(spot);
            });
            EventBus.$on(CONFIG.EVENT_BUS_CHECKIN_OPEN_SPOT, (spot) => {
                this.onCheckinOpenSpot(spot);
            });
        },
        unregisterEventListener() {
            EventBus.$off(CONFIG.EVENT_BUS_REFRESH_SPOT);
            EventBus.$off(CONFIG.EVENT_BUS_UPDATE_SPOT_ON_MAP);
            EventBus.$off(CONFIG.EVENT_BUS_CHECKIN_OPEN_SPOT);
        },
    },

    mounted() {
        this.registerEventListener();
    },
    activated() {
        this.pageActive = true;
    },
    deactivated() {
        this.pageActive = false;
    },
    beforeDestroy() {
        // do not use this because overlays of type TasksPage will remove the task refresh
        //this.unregisterEventListener();
    },
};
</script>

<style lang="scss" scoped>
.tasks-page-wrapper {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
}

.menu-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.menu-title-text {
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.menu-title-actions {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
}
</style>
